import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getDocSeries, updateDocSeries, type DocSeries } from "@/shared/api/series";

/** Number field driven by company series mode — plan/23 */
export function DocNumberField(props: {
  companySlug: string;
  docType: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const { companySlug, docType, value, onChange } = props;
  const [series, setSeries] = useState<DocSeries | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getDocSeries(companySlug, docType)
      .then((d) => {
        if (cancelled) return;
        setSeries(d.series);
        if (d.series.mode === "manual_prefilled" && !value) {
          onChange(d.series.suggested);
        }
        if (d.series.mode === "auto") {
          onChange("");
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed");
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, docType]);

  if (!series) {
    return (
      <p className="text-xs text-[var(--color-muted)]">
        {error ?? "Loading number series…"}
      </p>
    );
  }

  if (series.mode === "auto") {
    return (
      <div className="text-sm">
        <span className="text-[var(--color-muted)]">Number</span>
        <div className="mt-1 font-mono text-[var(--color-muted)]">
          Auto · next {series.suggested}
        </div>
        <button
          type="button"
          className="mt-1 text-xs text-[var(--color-primary)] underline-offset-2 hover:underline"
          onClick={() => {
            void updateDocSeries(companySlug, docType, {
              mode: "manual_prefilled",
            }).then((d) => {
              setSeries(d.series);
              onChange(d.series.suggested);
            });
          }}
        >
          Advanced: edit numbers
        </button>
      </div>
    );
  }

  return (
    <div>
      <Label htmlFor={`doc-number-${docType}`}>
        Number
        {series.mode === "manual" ? " (required)" : " (editable)"}
      </Label>
      <Input
        id={`doc-number-${docType}`}
        className="mt-1 font-mono"
        value={value}
        placeholder={series.suggested}
        required={series.mode === "manual"}
        onChange={(e) => onChange(e.target.value)}
      />
      <span className="mt-1 block text-xs text-[var(--color-muted)]">
        Suggested {series.suggested}
        {" · "}
        <button
          type="button"
          className="text-[var(--color-primary)] underline-offset-2 hover:underline"
          onClick={() => onChange(series.suggested)}
        >
          Use suggested
        </button>
        {" · "}
        <button
          type="button"
          className="text-[var(--color-primary)] underline-offset-2 hover:underline"
          onClick={() => {
            void updateDocSeries(companySlug, docType, { mode: "auto" }).then(
              (d) => {
                setSeries(d.series);
                onChange("");
              },
            );
          }}
        >
          Back to auto
        </button>
      </span>
    </div>
  );
}
